import React, { useEffect, useRef, useState } from "react";
import { useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { logOut } from "../../helpers/configRoutes/backend_helper";

const Header = () => {
    const navigate = useNavigate();
    const user = useSelector((state) => state.Login.user);
    const [menuItems, setMenuItems] = useState([
        { name: 'Inicio', link: '/inicio', icon: 'bar_chart_4_bars', activeRoutes: ['/inicio'] },
        { name: 'Usuarios', link: '/usuarios', icon: 'groups_3' },
        { name: 'Vendedores', link: '/vendedores', icon: 'real_estate_agent' },
        { name: 'Departamentos', link: '/departamentos', icon: 'category' },
        { name: 'Áreas', link: '/areas', icon: 'category_search' },
        { name: 'Productos', link: '/productos', icon: 'barcode' },
        { name: 'Ventas', link: '/ventas', icon: 'attach_money', activeRoutes: ['/ventas', '/ventas/nueva', '/venta/', '/ventas/editar'] },
        { name: 'Clientes', link: '/clients-follow', icon: 'barcode' },
    ]);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
    const location = useLocation();
    const profileRef = useRef(null);

    const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
    const toggleProfileMenu = () => setIsProfileMenuOpen(!isProfileMenuOpen);

    // Cerrar menús al cambiar de ruta
    useEffect(() => {
        setIsMenuOpen(false);
        setIsProfileMenuOpen(false);
    }, [location]);

    // Cerrar el menú de perfil al hacer click afuera
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (profileRef.current && !profileRef.current.contains(e.target)) {
                setIsProfileMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogout = async () => {
        const response = await logOut();
        localStorage.removeItem('isAuth');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
    };

    const isActiveRoute = (item) => {
        const routes = item.activeRoutes || [item.link];
        return routes.some(r => location.pathname === r || location.pathname.startsWith(r));
    };

    return (
        <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 dark:bg-fr-dark/80 border-b border-slate-200/70 dark:border-white/10 shadow-[0_1px_20px_-10px_rgba(0,0,0,0.1)]">
            {/* Línea de acento superior */}
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-fr-primary to-transparent"></div>

            <div className="flex justify-between items-center px-4 lg:px-6 py-3">
                {/* Lado izquierdo: hamburguesa + logo */}
                <div className="flex items-center gap-3">
                    {/* Hamburguesa móvil */}
                    <button
                        onClick={toggleMenu}
                        className="lg:hidden w-10 h-10 flex items-center justify-center rounded-xl text-fr-dark dark:text-fr-light hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
                    >
                        <span className="material-symbols-outlined">{isMenuOpen ? 'close' : 'menu'}</span>
                    </button>

                    {/* Logo */}
                    <div className="hidden lg:flex items-center gap-3">
                        <img src="/images/lexsys_logo.svg" alt="Logo" className="h-12 w-auto" />
                    </div>
                </div>

                {/* Menú principal (desktop) */}
                <nav className="hidden lg:flex items-center gap-1 mx-6 flex-1 justify-center">
                    {menuItems.map((item, i) => {
                        const active = isActiveRoute(item);
                        return (
                            <Link
                                key={i}
                                to={item.link}
                                className={`relative flex items-center gap-2 text-sm px-3.5 py-2 rounded-xl transition-all duration-200
                                    ${active
                                        ? 'text-fr-primary bg-fr-primary/10 font-semibold'
                                        : 'text-slate-600 dark:text-slate-300 hover:text-fr-dark dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/5'
                                    }`}
                            >
                                <span className="material-symbols-outlined text-[18px]">{item.icon}</span>
                                <span className="tracking-tight">{item.name}</span>
                                {active && (
                                    <span className="absolute -bottom-[13px] left-1/2 -translate-x-1/2 w-8 h-[3px] bg-gradient-to-r from-fr-primary to-fr-hover rounded-full"></span>
                                )}
                            </Link>
                        );
                    })}
                </nav>

                {/* Lado derecho: acciones + perfil */}
                <div className="flex items-center gap-2">
                    {/* Botón de notificaciones (decorativo, ya que tu lógica original no lo tenía) */}
                    <button className="hidden sm:flex relative w-10 h-10 items-center justify-center rounded-xl text-slate-500 hover:text-fr-primary hover:bg-slate-100 dark:hover:bg-white/5 transition-colors">
                        <span className="material-symbols-outlined text-[22px]">notifications</span>
                        <span className="absolute top-2 right-2.5 w-2 h-2 rounded-full bg-fr-danger ring-2 ring-white dark:ring-fr-dark"></span>
                    </button>

                    {/* Separador */}
                    <div className="hidden sm:block w-px h-7 bg-slate-200 dark:bg-white/10 mx-1"></div>

                    {/* Perfil */}
                    <div className="relative" ref={profileRef}>
                        <button
                            onClick={toggleProfileMenu}
                            className="flex items-center gap-3 pl-1 pr-2 py-1 rounded-full hover:bg-slate-100 dark:hover:bg-white/5 transition-all"
                        >
                            <div className="relative">
                                <img
                                    src={user?.imageProfile || "./images/usrAnonimo.png"}
                                    alt="Profile"
                                    className="w-9 h-9 rounded-full object-cover ring-2 ring-white dark:ring-white/10 shadow-sm"
                                />
                                <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-fr-dark"></span>
                            </div>
                            <div className="hidden sm:block text-left">
                                <p className="text-sm font-semibold text-fr-dark dark:text-fr-light leading-tight tracking-tight">
                                    {user?.first_name}
                                </p>
                                <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-tight">
                                    {user?.role}
                                </p>
                            </div>
                            <span className={`material-symbols-outlined text-[20px] text-slate-400 transition-transform duration-200 ${isProfileMenuOpen ? 'rotate-180' : ''}`}>
                                expand_more
                            </span>
                        </button>

                        {/* Dropdown perfil */}
                        <div className={`absolute right-0 mt-2 w-64 origin-top-right transition-all duration-200 ${isProfileMenuOpen
                            ? 'opacity-100 scale-100 pointer-events-auto'
                            : 'opacity-0 scale-95 pointer-events-none'
                            }`}>
                            <div className="bg-white dark:bg-fr-dark border border-slate-200/70 dark:border-white/10 rounded-2xl shadow-2xl shadow-slate-900/10 overflow-hidden">
                                {/* Header del dropdown */}
                                <div className="px-4 py-4 bg-gradient-to-br from-fr-primary/5 to-transparent border-b border-slate-100 dark:border-white/5">
                                    <div className="flex items-center gap-3">
                                        <img
                                            src={user?.imageProfile || "./images/usrAnonimo.png"}
                                            alt="Profile"
                                            className="w-11 h-11 rounded-full object-cover ring-2 ring-white shadow-md"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-bold text-fr-dark dark:text-fr-light truncate tracking-tight">
                                                {user?.first_name}
                                            </p>
                                            <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                                                {user?.email || user?.role}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Acciones */}
                                <div className="p-2">
                                    <button
                                        onClick={handleLogout}
                                        className="group w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-fr-danger hover:text-white transition-all"
                                    >
                                        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-fr-danger/10 text-fr-danger group-hover:bg-white/20 group-hover:text-white transition-all">
                                            <span className="material-symbols-outlined text-[18px]">logout</span>
                                        </div>
                                        <span className="text-sm font-medium">Cerrar sesión</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Menú lateral móvil (drawer) */}
            <div className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out ${isMenuOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="border-t border-slate-200/70 dark:border-white/10 bg-white/95 dark:bg-fr-dark/95 backdrop-blur-xl">
                    <nav className="flex flex-col p-3 gap-1">
                        {menuItems.map((item, i) => {
                            const active = isActiveRoute(item);
                            return (
                                <Link
                                    key={i}
                                    to={item.link}
                                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all
                                        ${active
                                            ? 'bg-gradient-to-r from-fr-primary to-fr-hover text-white shadow-md shadow-fr-primary/25 font-semibold'
                                            : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5'
                                        }`}
                                >
                                    <div className={`w-9 h-9 flex items-center justify-center rounded-lg
                                        ${active ? 'bg-white/20' : 'bg-slate-100 dark:bg-white/5 text-fr-primary'}
                                        `}>
                                        <span className="material-symbols-outlined text-[20px]">{item.icon}</span>
                                    </div>
                                    <span className="text-sm">{item.name}</span>
                                </Link>
                            );
                        })}
                    </nav>
                </div>
            </div>
        </header>
    );
};

export default Header;
